import type { ID } from '@/model/Common.model';
import type { MetadataItem } from '@/model/Metadata.model';
import { ApiService } from '@/support/ApiService';
import { _notNil, _isNil, safelyDecodeURIComponent } from '@/littledash';
import { isEmpty } from 'lodash';

export const findMetaIndexById = (data: Array<ExtendedMetadataItem>, id?: number | string): number =>
  data.findIndex((meta) => String(meta.id) === String(id));

export interface Errors {
  errors: Error;
  index: number;
  id: ID;
  type: string;
}

export interface Error {
  options?: string[][];
  title?: string[];
  field_type?: string[];
  general?: string[];
}

export interface ExtendedMetadataItem extends Partial<MetadataItem> {
  new: boolean;
  expanded: boolean;
  index: number;
  archived?: boolean;
  api_id?: string;
  required?: boolean;
  value?: string;
  glossary_id?: ID;
  created_at?: string;
  samples_required?: boolean;
}

export function sortActiveFirst<T extends { active?: boolean }>(items: T[]): T[] {
  return items.slice().sort((a, b) => {
    if (a.active === b.active) {
      return 0;
    }
    return a.active ? -1 : 1;
  });
}

export function sortActiveFirstThenAlphabetical<T extends { active?: boolean; title?: string }>(items: T[]): T[] {
  return items.slice().sort((a, b) => {
    if (a.active !== b.active) {
      return (a.active ?? true) ? -1 : 1;
    }

    const titleA = (a.title ?? '').toLowerCase().trim();
    const titleB = (b.title ?? '').toLowerCase().trim();

    if (titleA === titleB) {
      return 0;
    }

    return titleA < titleB ? -1 : 1;
  });
}

export const handlePayload = ({ title, type, field_type, options, active, required }: ExtendedMetadataItem) => {
  const payload: any = {
    title,
    type,
    field_type,
    active,
    required,
  };

  if (field_type === 'select' || field_type === 'multi_select' || field_type === 'lookup_multi_select') {
    payload.options = options || [];
  }

  if (field_type === 'multi_select' || field_type === 'lookup_multi_select') {
    payload.options = payload.options.map((value: string) => encodeURIComponent(value));
  }

  return payload;
};

export const createSingleMetadataItem = async (metadata: ExtendedMetadataItem): Promise<MetadataItem | undefined> => {
  const payload = handlePayload(metadata);

  const response = await ApiService.call({
    endpoint: 'POST /api/v1/metadata-glossary',
    body: payload,
    options: {
      onError: {
        preserveOriginalError: true,
      },
    },
  });

  const createdItem = response.body;

  return createdItem as MetadataItem;
};

export const updateSingleMetadataItem = async (metadata: ExtendedMetadataItem): Promise<MetadataItem | undefined> => {
  const payload = handlePayload(metadata);

  const metadataApiId = metadata.api_id;

  if (_isNil(metadataApiId) || isEmpty(metadataApiId)) {
    throw new Error('No valid ID found for metadata item');
  }

  const response = await ApiService.call({
    endpoint: 'PATCH /api/v1/metadata-glossary/{metadataApiId}',
    path: { metadataApiId: metadataApiId },
    body: payload,
    options: {
      onError: {
        preserveOriginalError: true,
      },
    },
  });

  const updatedItem = response.body;

  return updatedItem as MetadataItem;
};

export const toggleArchiveMetadataItem = async (metadata: ExtendedMetadataItem): Promise<MetadataItem | undefined> => {
  const payload = handlePayload({
    ...metadata,
    active: !metadata.active,
  });

  const metadataApiId = metadata.api_id;

  if (_isNil(metadataApiId) || isEmpty(metadataApiId)) {
    throw new Error('No valid ID found for metadata item');
  }

  const response = await ApiService.call({
    endpoint: 'PATCH /api/v1/metadata-glossary/{metadataApiId}',
    path: { metadataApiId: metadataApiId },
    body: payload,
    options: {
      onError: {
        preserveOriginalError: true,
      },
    },
  });

  const updatedItem = response.body;

  return updatedItem as MetadataItem;
};

export const decodeOptionsForDisplay = (item: ExtendedMetadataItem): ExtendedMetadataItem => {
  const { field_type, options } = item;

  if (field_type === 'multi_select' && _notNil(options)) {
    return {
      ...item,
      options: options.map((option: string) => safelyDecodeURIComponent(option)),
    };
  }

  return item;
};
